import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { appendToSheet } from "../utils/googleSheets/sheetsService";

/**
 * Custom hook to track autofill performance of form fields
 *
 * This hook compares the renewData from Redux with the actual form field values
 * to determine which fields were successfully autofilled when the component mounts.
 * Results are logged to Google Sheets for performance analysis.
 *
 * @param {object} input - Current form input state
 * @param {boolean} hasAutoLookuped - Flag indicating if auto-lookup has completed
 */
export function useAutofillTracking(input, hasAutoLookuped) {
  const hasTrackedRef = useRef(false);
  const renewData = useSelector((state) => state.userData.renewPapersData);
  const user = useSelector((state) => state.userData.personalInfo);

  useEffect(() => {
    // Only track once per component mount
    if (hasTrackedRef.current) return;

    // Wait for auto-lookup to complete before tracking
    // (or wait a reasonable time if renewData is empty)
    if (!hasAutoLookuped && renewData?.getNiconInsuranceRes) return;

    // Small delay to ensure all form fields are populated
    const trackingTimeout = setTimeout(() => {
      trackAutofillPerformance();
    }, 1000);

    return () => clearTimeout(trackingTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewData, hasAutoLookuped]);

  const trackAutofillPerformance = async () => {
    try {
      hasTrackedRef.current = true;

      const niconData = renewData?.getNiconInsuranceRes || {};

      // Define the fields we're tracking and their source values from renewData
      const fieldMappings = {
        fullname:
          niconData?.insured_name || niconData?.full_name || niconData?.insured,
        phone:
          niconData?.phone || niconData?.phone_no || niconData?.phoneNumber,
        chasisNo:
          niconData?.chasis_no || niconData?.chassis_no || niconData?.chassis,
        engineNo: niconData?.engine_no || niconData?.engineNo,
        email: niconData?.email,
        color: niconData?.color,
        year: niconData?.year_of_make || niconData?.year,
        // Make and model are auto-looked up, so we check if they exist in renewData
        make:
          niconData?.vehicle_make_custom ||
          niconData?.make ||
          niconData?.vehicleMake,
        model: niconData?.model || niconData?.vehicleModel,
      };

      // Check which fields were successfully autofilled
      const autofillResults = {};
      let autofilledCount = 0;
      const totalFields = Object.keys(fieldMappings).length;

      Object.keys(fieldMappings).forEach((fieldName) => {
        const sourceValue = fieldMappings[fieldName];
        const currentValue = input[fieldName];

        // A field is considered autofilled if:
        // 1. There was a value in renewData for it
        // 2. The current input has a value (could be the same or transformed)
        const isAutofilled = Boolean(sourceValue) && Boolean(currentValue);

        autofillResults[fieldName] = isAutofilled;
        if (isAutofilled) autofilledCount++;
      });

      // Calculate autofill percentage
      const autofillPercentage = (
        (autofilledCount / totalFields) *
        100
      ).toFixed(2);

      // Prepare data row for Google Sheets
      const timestamp = new Date().toISOString();
      const rowData = [
        timestamp, // A: Timestamp
        user?.email || "N/A", // B: Agent Email
        renewData?.plateNumber || input.plate_no || "N/A", // C: Plate Number
        renewData?.insuranceType || "N/A", // D: Insurance Type
        autofillResults.fullname ? "Yes" : "No", // E: Full Name (Autofilled)
        autofillResults.phone ? "Yes" : "No", // F: Phone (Autofilled)
        autofillResults.chasisNo ? "Yes" : "No", // G: Chassis (Autofilled)
        autofillResults.engineNo ? "Yes" : "No", // H: Engine (Autofilled)
        autofillResults.email ? "Yes" : "No", // I: Email (Autofilled)
        autofillResults.color ? "Yes" : "No", // J: Color (Autofilled)
        autofillResults.year ? "Yes" : "No", // K: Year (Autofilled)
        autofillResults.make ? "Yes" : "No", // L: Make (Autofilled)
        autofillResults.model ? "Yes" : "No", // M: Model (Autofilled)
        autofilledCount, // N: Total Fields Autofilled
        totalFields, // O: Total Fields
        `${autofillPercentage}%`, // P: Autofill Percentage
        renewData?.dataSource || "UNKNOWN", // Q: Data Source (which endpoint returned data)
      ];

      // Log to console for debugging
      console.log("Autofill Performance Tracking:", {
        hasRenewData: Boolean(renewData?.getNiconInsuranceRes),
        dataSource: renewData?.dataSource || "UNKNOWN",
        autofilledCount,
        totalFields,
        autofillPercentage: `${autofillPercentage}%`,
        results: autofillResults,
      });

      // Send to Google Sheets (non-blocking, with error handling)
      appendToSheet(rowData).catch((error) => {
        console.error("Failed to log autofill data to Google Sheets:", error);
        // Don't throw - we don't want to break the UI if logging fails
      });
    } catch (error) {
      console.error("Error tracking autofill performance:", error);
      // Reset the flag so it can retry if needed
      hasTrackedRef.current = false;
    }
  };

  // Return tracking status for debugging if needed
  return {
    hasTracked: hasTrackedRef.current,
  };
}
